<template>
  <div>
    <Breadcrumb
      :items="breadcrumb"
      with-brodos
    />
    <ProductDetail
      v-if="offer?.product"
      :offer="offer"
      :transformed-alternatives="transformedAlternatives"
    />
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { setResponseHeader } from 'h3';
import { truncate } from 'lodash-es';
import { isSlug } from '~~/server/apollo/goliath-variable-validators';
import { useRuntimeConfig, useNuxtApp, useHead, useRoute } from '#imports';
import fetchOfferBySlugs from '~/lib/goliath/offer-by-slugs';
import { getMainImageFromOffer } from '~/lib/goliath-media';

import { generateJsonLdData, transformOfferAlternatives } from '@i22team/rocket';
import { getHeader } from '~/helpers/page-header-compositon';
import ProductDetail from '~/components/product-detail.vue';
import Breadcrumb, { type BreadcrumbItem } from '~/components/breadcrumb.vue';
import { calculateLastModifiedOffer } from '#shared/last-modified-offer';

import { TrackAsync } from '~/helpers/page-tracking';
import { isProductPageIndexed } from '#shared/page-index-helper';
import { useDowntimesStore } from '~/stores/downtimes';
import type { RouteLocationNormalized } from '#vue-router';

const nuxtApp = useNuxtApp();
const config = useRuntimeConfig();
const $route = useRoute();
const event = useRequestEvent();

const { maintenanceDowntime } = storeToRefs(useDowntimesStore());

definePageMeta({
  name: 'ProductDetailPage',
  validate: (route: RouteLocationNormalized) => {
    const slug = route?.params?.slug;
    if (!slug || !isSlug(slug)) {
      return {
        status: 404,
        // Nuxt overwrites `data` in validate results, so `statusText` is the marker for error.vue.
        statusText: 'Invalid slug',
      };
    }
    route.meta.slug = slug;
    return true;
  },
});

const productSlug = ref<string>((($route?.meta?.slug || $route?.params?.slug) as string) || '');

if (!productSlug.value) {
  showError({ status: 404, message: 'No product slug' });
}

watch(
  maintenanceDowntime,
  () => {
    if (maintenanceDowntime.value) {
      if (event) {
        // Make this page be stored not so long in nginx.
        setResponseHeader(event, 'Cache-Control', 'public, max-age=30');
      }
      if (!nuxtApp.$maintenanceRedirectUrl) {
        // store current url in context - works only csr.
        nuxtApp.provide('maintenanceRedirectUrl', $route.fullPath);
      }

      return navigateTo('/maintenance');
    }
  },
  { immediate: true }
);

const { data: offer, error: errorData } = await useAsyncData(
  productSlug,
  (_nuxtApp, { signal }) =>
    fetchOfferBySlugs({ productSlug: productSlug.value }, true, false, signal),
  {
    watch: [productSlug],
  }
);

watch(
  errorData,
  () => {
    if (errorData.value) {
      if (errorData.value.statusCode === 404) {
        return showError({
          statusCode: 404,
          message: errorData.value?.message,
          data: { productSlug: productSlug.value },
        });
      }
      return showError(errorData.value);
    }
  },
  { immediate: true }
);

watch(
  offer,
  () => {
    if (offer.value) {
      // Product must have a primary category!
      if (!offer.value.product.family?.primaryCategory) {
        return showError({
          statusCode: 404,
          message: 'No primary category',
          data: { productSlug: productSlug.value },
        });
      }

      // Set last modified header on ssr (event is defined)
      if (event && !event.node?.res?.headersSent) {
        setResponseHeader(
          event,
          'last-modified',
          calculateLastModifiedOffer(offer.value || undefined, new Date(config.public.buildDate))
        );
      }
    }
  },
  {
    immediate: true,
  }
);

const transformedAlternatives = computed(() => {
  if (!offer.value?.productAlternatives) return [];
  return transformOfferAlternatives(offer.value.productAlternatives.filter(Boolean));
});

const seoProductName = computed(() => {
  if (!offer.value?.product) return undefined;
  const productHasAlternatives =
    (transformedAlternatives.value.filter((entry) =>
      entry.properties.some((property) => property.items.length > 1)
    )?.length || 0) > 0;

  return productHasAlternatives ? offer.value.product.name : offer.value.product.family?.name;
});

const seoDescription = computed(() => {
  if (!offer.value) return '';
  const uspsText = offer.value.usps
    ?.slice(0, 3)
    ?.map((usp) => `${usp} ✓`)
    ?.join(' ');
  const descr = ['Jetzt online bestellen:', seoProductName.value, '-', uspsText].join(' ');
  return truncate(descr, { length: 155, separator: ' ' });
});

getHeader({
  title: computed(() => `${seoProductName.value} kaufen`),
  description: seoDescription,
  isIndexed: computed(() => isProductPageIndexed(offer.value)),
  ogImage: computed(() => getMainImageFromOffer(offer.value || undefined)?.medium),
});

// json-ld
useHead(() => {
  if (!offer.value?.product) return {};
  return {
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(
          generateJsonLdData<OfferWithProduct>(offer.value || undefined, config.public.baseUrl),
          null,
          ''
        ),
      },
    ],
  };
});

// Add Queue-it Waiting-Room Script for certain products
useHead(() => {
  if (
    !offer.value?.product?.articleNumber ||
    ![
      '99935627', // Anker USB-C Nano Powerbank Konnector
    ].includes(offer.value.product.articleNumber)
  )
    return {};
  return {
    script: [
      {
        src: '//static.queue-it.net/script/queueclient.min.js',
        type: 'text/javascript',
      },
      {
        src: '//static.queue-it.net/script/queueconfigloader.min.js',
        type: 'text/javascript',
        'data-queueit-c': 'magenta',
      },
    ],
  };
});

onMounted(() => {
  TrackAsync(
    {
      page_type: 'product.individual',
      items: [offer.value],
    },
    { delay: 100 }
  );
});

const getBrandRoute = (brandSlug: string) => {
  const primaryCategory = offer.value?.product?.family?.primaryCategory?.path;
  return `/${primaryCategory}/_${brandSlug.toLowerCase()}`;
};

const breadcrumb = computed(() => {
  const items: BreadcrumbItem[] = [];
  if (!offer.value?.product) {
    return items;
  }
  const { product } = offer.value;
  const bundleProduct = getBundleProduct(product);
  const standardProduct = getStandardProduct(product);
  if (bundleProduct?.family?.name) {
    items.push({ label: bundleProduct.family.name, to: $route.path });
  } else if (standardProduct?.brand?.name && standardProduct.brand.slug) {
    items.push({
      label: standardProduct.brand.name,
      to: getBrandRoute(standardProduct.brand.slug),
    });
  }
  if (standardProduct) {
    items.push({
      label: `${standardProduct.model || ''} ${standardProduct.variantName || ''}`.trim(),
      to: $route.path,
    });
  }

  return items.filter((i) => i.label !== '' && i.to !== '');
});
</script>
